import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { claims, isKeycloakConfigured } from '../auth/keycloak'
import type { Scope } from '../auth/scope'
import { expandMemberships } from '../auth/memberships'
import { api, ORGANIZATION_HEADER } from './client'
import type { Me, RanchRef, Setting, TenantTheme, TenantThemeRequest } from './types'

/** Who the API thinks the caller is. Keyed by subject so a re-login never serves a stale user. */
export function useMe(enabled = true) {
  const subject = isKeycloakConfigured() ? (claims().sub ?? 'unknown') : 'stub'

  return useQuery({
    queryKey: ['me', subject],
    queryFn: fetchMe,
    staleTime: 60_000,
    retry: 1,
    enabled,
  })
}

/** GET /me, with organization-wide memberships expanded into their ranches. */
export async function fetchMe() {
  const response = await api.get<Me>('/api/v1/me', { scope: null })
  const memberships = await expandMemberships(response.data.memberships ?? [], async (orgId) => {
    const { data } = await api.get<RanchRef[]>(`/api/v1/organizations/${orgId}/ranches`, {
      scope: null,
      headers: { [ORGANIZATION_HEADER]: orgId },
    })
    return data ?? []
  })
  return { ...response, data: { ...response.data, memberships } }
}

export const themeKey = (organizationId: string | null) => ['theme', organizationId] as const

/** The organization's theme, or null for the platform look (204). Disabled offline: the provider reads the cache. */
export function useTenantTheme(organizationId: string | null, enabled = true) {
  return useQuery({
    queryKey: themeKey(organizationId),
    queryFn: async () => {
      const { data } = await api.get<TenantTheme | undefined>(
        `/api/v1/organizations/${organizationId}/theme`,
      )
      return data ?? null
    },
    enabled: enabled && organizationId !== null,
    staleTime: 5 * 60_000,
    retry: 1,
  })
}

export function useSaveTheme(organizationId: string | null) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (request: TenantThemeRequest) => {
      const { data } = await api.put<TenantTheme>(
        `/api/v1/organizations/${organizationId}/theme`,
        request,
      )
      return data
    },
    onSuccess: (theme) => client.setQueryData(themeKey(organizationId), theme),
  })
}

export function useResetTheme(organizationId: string | null) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      await api.del(`/api/v1/organizations/${organizationId}/theme`)
    },
    onSuccess: () => client.setQueryData(themeKey(organizationId), null),
  })
}

export function useUploadLogo(organizationId: string | null) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async ({ file, variant }: { file: File; variant: 'light' | 'dark' }) => {
      const form = new FormData()
      form.append('file', file)
      form.append('variant', variant)
      const { data } = await api.uploadForm<TenantTheme>(
        `/api/v1/organizations/${organizationId}/theme/logo`,
        form,
      )
      return data
    },
    onSuccess: (theme) => client.setQueryData(themeKey(organizationId), theme),
  })
}

export function useSettings(scope: Scope | null, enabled = true) {
  return useQuery({
    queryKey: ['settings', scope?.organizationId, scope?.ranchId],
    queryFn: async () =>
      (await api.get<Setting[]>(`/api/v1/ranches/${scope?.ranchId}/settings`)).data,
    enabled: enabled && scope !== null,
    staleTime: 60_000,
  })
}

/** A new value for one ranch setting (manager or owner); the list is re-read after. */
export function useSaveSetting(scope: Scope | null) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: async ({ key, value }: { key: string; value: unknown }) =>
      (
        await api.put<Setting>(`/api/v1/ranches/${scope?.ranchId}/settings/${key}`, {
          value,
        })
      ).data,
    onSuccess: () =>
      client.invalidateQueries({ queryKey: ['settings', scope?.organizationId, scope?.ranchId] }),
  })
}
